import { Link } from '@tanstack/react-router';
import type { TFunction } from 'i18next';
import type { ListOrgFunctionsItem } from '@bliks-io/contracts';
import { createColumnHeader, renderFallback, type DataTableColumnDef } from '@bliks-io/ui';
import { orgFunctionDetailRoute } from '@/lib/orgFunctionRoutes';

/**
 * Column builders shared by the OrgFunction catalog and the department detail
 * page so the name-link, lead, and member-count cells stay identical in both
 * tables. `memberCount` is the server-derived value from the list endpoint —
 * membership is never stored, so it must not be re-derived client-side.
 */

export function buildOrgFunctionNameColumn(
  t: TFunction,
  slug: string
): DataTableColumnDef<ListOrgFunctionsItem> {
  return {
    accessorKey: 'name',
    meta: { label: t('common.Name'), filterType: 'text' },
    header: createColumnHeader(t('common.Name')),
    cell: ({ row }) => (
      <Link
        to={orgFunctionDetailRoute(row.original.kind)}
        params={{ slug, orgFunctionId: row.original._id }}
        className="font-medium hover:underline"
      >
        {row.original.name}
      </Link>
    ),
    enableColumnFilter: true
  };
}

export function buildOrgFunctionLeadColumn(t: TFunction): DataTableColumnDef<ListOrgFunctionsItem> {
  return {
    id: 'lead',
    accessorFn: (row) => (row.lead ? `${row.lead.firstName} ${row.lead.lastName}` : undefined),
    meta: { label: t('organization.orgFunctions.lead') },
    header: createColumnHeader(t('organization.orgFunctions.lead')),
    cell: ({ row }) =>
      renderFallback(
        row.original.lead
          ? `${row.original.lead.firstName} ${row.original.lead.lastName}`
          : undefined
      )
  };
}

export function buildOrgFunctionMemberCountColumn(
  t: TFunction
): DataTableColumnDef<ListOrgFunctionsItem> {
  return {
    id: 'memberCount',
    accessorFn: (row) => row.memberCount,
    meta: { label: t('organization.orgFunctions.memberCount') },
    header: createColumnHeader(t('organization.orgFunctions.memberCount')),
    cell: ({ row }) => row.original.memberCount
  };
}
